const readFiniteNumber = (value) => (Number.isFinite(value) ? value : 0)

/**
 * `pl_total` vem zerado quando a cotização do dia ainda não gravou a coluna
 * `pl` dos ativos. Nesse caso o valor confiável é `pl_cotizacao_aawz`.
 */
const isPosicaoBlockZeroed = (posicao) => {
  if (!posicao) return false
  const grupos = Array.isArray(posicao.grupos) ? posicao.grupos : []
  if (!grupos.length) return false
  return Number(posicao.pl_total) === 0
}

export const isPosicaoZeroed = (walletAnalysis) =>
  isPosicaoBlockZeroed(walletAnalysis && walletAnalysis.posicao_completa)

export const resolveAtivoPl = (ativo, zeroed) => {
  if (!ativo) return 0
  return zeroed
    ? readFiniteNumber(ativo.pl_cotizacao_aawz)
    : readFiniteNumber(ativo.pl)
}

export const resolveGrupoPl = (grupo, zeroed) => {
  if (!grupo) return 0
  if (!zeroed) return readFiniteNumber(grupo.pl)
  const ativos = Array.isArray(grupo.ativos) ? grupo.ativos : []
  return ativos.reduce((acc, ativo) => acc + resolveAtivoPl(ativo, true), 0)
}

/**
 * PL total do bloco `posicao_completa` — a fonte de patrimônio das telas.
 *
 * É a **posição atual** (snapshot do banco, inclui ativo sem série no S3), não
 * `portfolio.pl_fim`, que é o último ponto da série cotizada. As duas divergem
 * sempre que há ativo posicionado sem série: no grupo AAWZ do escritório 83 a
 * diferença chega a R$ 1,4 milhão em um único membro.
 *
 * @param {Object} [posicao] Bloco `posicao_completa`.
 * @returns {Number|null} `null` quando o bloco não veio no payload.
 */
export const resolvePosicaoBlockPlTotal = (posicao) => {
  if (!posicao) return null
  const plTotal = Number(posicao.pl_total)
  if (!isPosicaoBlockZeroed(posicao)) {
    return Number.isFinite(plTotal) ? plTotal : null
  }
  const grupos = Array.isArray(posicao.grupos) ? posicao.grupos : []
  return grupos.reduce((acc, grupo) => acc + resolveGrupoPl(grupo, true), 0)
}

export const resolvePosicaoPlTotal = (walletAnalysis) =>
  resolvePosicaoBlockPlTotal(walletAnalysis && walletAnalysis.posicao_completa)

/**
 * O mesmo PL, quebrado por titular do ativo.
 *
 * Existe para a carteira do GRUPO: o payload consolidado traz os ativos de
 * todos os membros com `id_cliente` em cada um, então o PL por membro sai daqui
 * pela MESMA regra que produz o total da tela individual — inclusive o fallback
 * de posição zerada. Derivar a linha do membro de outra fonte
 * (`breakdown.pl_fim`) faz a mesma carteira aparecer com dois patrimônios
 * diferentes conforme a tela.
 *
 * Somar PL é aditivo por definição e não recalcula métrica nenhuma: o que não
 * pode ser agregado no cliente é rentabilidade, volatilidade e Sharpe.
 *
 * @param {Object} [posicao] Bloco `posicao_completa`.
 * @returns {Object} Mapa `{ '<id_cliente>': pl }`.
 */
export const resolvePosicaoPlPorCliente = (posicao) => {
  const porCliente = {}
  if (!posicao) return porCliente

  const zeroed = isPosicaoBlockZeroed(posicao)
  const grupos = Array.isArray(posicao.grupos) ? posicao.grupos : []

  for (const grupo of grupos) {
    const ativos = grupo && Array.isArray(grupo.ativos) ? grupo.ativos : []
    for (const ativo of ativos) {
      if (!ativo) continue
      const chave = String(ativo.id_cliente)
      const acumulado = porCliente[chave] === undefined ? 0 : porCliente[chave]
      porCliente[chave] = acumulado + resolveAtivoPl(ativo, zeroed)
    }
  }

  return porCliente
}
